import { Injectable, signal } from '@angular/core'

@Injectable({
  providedIn: 'root',
})
export class AudioService {
  private audio: HTMLAudioElement | null = null

  // Signal to track playing state
  isPlaying = signal<boolean>(false)

  constructor() {
    console.log('🎵 AudioService initialized')
  }

  /**
   * Initialize audio with given source
   */
  initAudio(src: string): void {
    if (!this.audio) {
      this.audio = new Audio(src)
      this.audio.preload = 'none';
      this.audio.loop = true
      this.audio.volume = 0.5 // 50% volume by default

      // Update signal when audio ends (shouldn't happen with loop, but safety)
      this.audio.addEventListener('ended', () => {
        this.isPlaying.set(false)
        console.log('🎵 Audio ended')
      })

      // Handle audio errors
      this.audio.addEventListener('error', (e) => {
        console.error('❌ Audio error:', e)
        this.isPlaying.set(false)
      })

      console.log('🎵 Audio initialized:', src)
    }
  }

  /**
   * Toggle play/pause
   */
  toggle(): void {
    if (!this.audio) {
      console.warn('⚠️ Audio not initialized')
      return
    }

    if (this.isPlaying()) {
      this.pause()
    } else {
      this.play()
    }
  }

  /**
   * Play audio
   */
  play(): void {
    if (!this.audio) {
      console.warn('⚠️ Audio not initialized')
      return
    }

    this.audio.load()

    this.audio
      .play()
      .then(() => {
        this.isPlaying.set(true)
        console.log('▶️ Audio playing')
      })
      .catch((error) => {
        console.error('❌ Failed to play audio:', error)
        this.isPlaying.set(false)
      })
  }

  /**
   * Pause audio
   */
  pause(): void {
    if (!this.audio) {
      console.warn('⚠️ Audio not initialized')
      return
    }

    this.audio.pause()
    this.isPlaying.set(false)
    console.log('⏸️ Audio paused')
  }

  /**
   * Stop audio and reset to beginning
   */
  stop(): void {
    if (!this.audio) {
      console.warn('⚠️ Audio not initialized')
      return
    }

    this.audio.pause()
    this.audio.currentTime = 0
    this.isPlaying.set(false)
    console.log('⏹️ Audio stopped')
  }

  /**
   * Set volume (0 to 1)
   */
  setVolume(volume: number): void {
    if (!this.audio) {
      console.warn('⚠️ Audio not initialized')
      return
    }

    this.audio.volume = Math.max(0, Math.min(1, volume))
    console.log('🔊 Volume set to:', this.audio.volume)
  }

  /**
   * Cleanup on destroy
   */
  destroy(): void {
    if (this.audio) {
      this.stop()
      this.audio = null
      console.log('🗑️ Audio destroyed')
    }
  }
}
